import { Injectable } from '@angular/core';
import { S3FileService } from '../../../../../../../../goldstar-share/src/app/services/s3file-service';
import { getMimeTypeFromExtension } from '../utils/mime-utils';

/** Encapsulates the state and logic for opening artifacts and documents */
@Injectable({ providedIn: 'root' })
export class ArtifactStateService {
	// ── Artifact panel visibility ──
	artifactOpen = false;

	// ── Content/artifact mode state ──
	activeArtifactTitle: string | null = null;
	activeArtifactContent: string | null = null;

	// ── Document viewer mode state ──
	activeDocumentBlob: Blob | null = null;
	activeDocumentContentType: string | null = null;

	constructor(private s3FileService: S3FileService) {}

	/** Reset all artifact state to closed/empty */
	closeArtifact(): void {
		this.artifactOpen = false;
		this.activeArtifactTitle = null;
		this.activeArtifactContent = null;
		this.activeDocumentBlob = null;
		this.activeDocumentContentType = null;
	}

	/** Open a markdown artifact in the panel */
	openArtifactContent(title: string, content: string): void {
		this.activeArtifactTitle = title;
		this.activeArtifactContent = content;
		this.activeDocumentBlob = null;
		this.activeDocumentContentType = null;
		this.artifactOpen = true;
	}

	/**
	 * Fetch a document from S3 and open it in the artifact panel.
	 * Returns an error message string if something went wrong, or null on success.
	 */
	async openDocument(event: { title: string; s3FileVersionGUID?: string }): Promise<string | null> {
		if (!event.s3FileVersionGUID) {
			return 'Could not load document: file reference is missing.';
		}
		try {
			const result = await this.s3FileService.fetchS3FilePreSignedUrl({ s3FileVersionGUID: event.s3FileVersionGUID, createDownloadLink: false });
			if (!result?.presignedUrl) {
				return 'Could not load document. Please try again.';
			}
			const fetchResp = await fetch(result.presignedUrl);
			if (!fetchResp.ok) {
				return 'Could not load document. Please try again.';
			}
			const mimeType = getMimeTypeFromExtension(result.fileType, result.fileName);
			const blob = new Blob([await fetchResp.arrayBuffer()], { type: mimeType });

			this.activeArtifactTitle = result.fileName || event.title;
			this.activeDocumentBlob = blob;
			this.activeDocumentContentType = mimeType;
			this.activeArtifactContent = null;

			this.artifactOpen = true;
			return null;
		} catch {
			return 'Could not load document. Please try again.';
		}
	}
}
